import { Home as HomeIcon, GraduationCap, Car, Briefcase, Wallet, Factory, Building2, HardHat, Landmark, Store, User, MessageCircle } from "lucide-react";
import PageHero, { ScrollCta } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import LeadForm from "@/components/LeadForm";
import { CONTACT } from "@/lib/api";

const INTRO =
  "At Evolve Elite, we structure capital to accelerate your growth. Whether you are an individual purchasing a new property or a corporation requiring multi-crore infrastructure finance, our advisory ensures fast processing, competitive rates, and seamless execution. We bridge the gap between borrowers and top-tier financial institutions to deliver tailored liquidity solutions.";

const RETAIL_LEDE =
  "Our consumer loan division is designed to secure your personal milestones with minimal friction and optimal terms.";

const RETAIL = [
  {
    icon: HomeIcon,
    title: "Home Loans",
    desc: "Secure financing for new residential purchases, property construction, or plot acquisitions with flexible repayment tenures and highly competitive interest rates.",
  },
  {
    icon: Building2,
    title: "Loan Against Property (LAP)",
    desc: "Unlock the dormant value of your residential or commercial real estate to access high-value, low-interest funding for personal obligations, debt consolidation, or significant expenses.",
  },
  {
    icon: GraduationCap,
    title: "Education Loans (Secured & Unsecured)",
    desc: "Fund domestic and international higher education seamlessly, ensuring financial constraints do not limit academic and career potential.",
  },
  {
    icon: Car,
    title: "New & Used Car Finance",
    desc: "Tailored auto loans featuring fast approvals, high loan-to-value ratios, and customized tenures for premium vehicles (+20 Lacs) and pre-owned cars.",
  },
  {
    icon: User,
    title: "Unsecured Personal Loans",
    desc: "Access immediate, collateral-free capital for medical emergencies, travel, or major life events with rapid disbursement protocols.",
  },
];

const BUSINESS_LEDE =
  "We empower modern enterprises with strategic capital infusion, allowing you to scale operations, acquire assets, and maintain robust daily cash flow.";

const BUSINESS = [
  {
    icon: Wallet,
    title: "Working Capital (CC/OD/TL)",
    desc: "Stabilize your day-to-day operations with customized Cash Credit, Overdraft, and Term Loan facilities designed to optimize your operational cash flow and manage inventory cycles.",
  },
  {
    icon: Briefcase,
    title: "Unsecured Business Loans",
    desc: "Fast-track your immediate growth requirements with collateral-free funding, ideal for inventory scaling, marketing expansions, or sudden operational expenses.",
  },
  {
    icon: Factory,
    title: "Machinery & Equipment Loans",
    desc: "Finance the acquisition, upgrade, or replacement of essential industrial and manufacturing equipment without depleting your primary working capital reserves.",
  },
  {
    icon: HardHat,
    title: "Project Funding & Construction Finance",
    desc: "Secure large-scale, complex capital structuring for real estate developments, commercial infrastructure, and long-term business expansion projects.",
  },
  {
    icon: Landmark,
    title: "Lease Rental Discounting (LRD)",
    desc: "Convert your future commercial rent receivables into immediate, upfront liquidity by securing term loans against the underlying value of your existing lease agreements.",
  },
  {
    icon: Store,
    title: "Commercial Purchase Loans",
    desc: "Fund the acquisition of new office spaces, retail outlets, or commercial plots to permanently expand your enterprise's physical footprint.",
  },
];

const LOAN_SERVICES = [
  "Home Loan", "Loan Against Property (LAP)", "Education Loan (Secured)", "Education Loan (Unsecured)",
  "New Car Finance", "Used Car Finance", "Personal Loan", "Working Capital (CC/OD/TL)",
  "Unsecured Business Loan", "Machinery & Equipment Loan", "Project Funding & Construction Finance",
  "Lease Rental Discounting (LRD)", "Commercial Purchase Loan",
];

const LoanCard = ({ item, dark = false }) => (
  <div
    className={`card-lift flex h-full flex-col rounded-3xl border p-7 ${dark ? "border-white/10 bg-[#122647]" : "border-slate-200 bg-white"}`}
    data-testid={`loan-card-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
  >
    <item.icon className={`h-8 w-8 ${dark ? "text-gold" : "text-gold-deep"}`} />
    <h3 className={`mt-5 font-display text-xl font-extrabold leading-snug tracking-tight ${dark ? "text-white" : "text-navy"}`}>
      {item.title}
    </h3>
    <p className={`mt-3 text-sm leading-relaxed ${dark ? "text-white/70" : "text-slate-600"}`}>{item.desc}</p>
  </div>
);

export default function Loans() {
  return (
    <div data-testid="loans-page">
      <PageHero
        eyebrow="Loans & Funding"
        title={["Loans &", "Funding."]}
        sub={INTRO}
      >
        <ScrollCta href="#apply" label="Apply for a Loan" testId="loan-apply-btn" />
        <a
          href={`${CONTACT.whatsapp}?text=${encodeURIComponent("Hello Evolve Elite, I'd like to check my loan eligibility.")}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:brightness-110 active:scale-[0.98]"
          data-testid="loan-whatsapp-eligibility-btn"
        >
          <MessageCircle className="h-4 w-4" /> Check Your Eligibility via WhatsApp
        </a>
      </PageHero>

      {/* RETAIL FINANCE */}
      <section className="py-16 sm:py-24 lg:py-28" data-testid="retail-finance-section">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Retail Finance</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">
              For your personal milestones.
            </h2>
            <p className="mt-4 max-w-2xl text-slate-600">{RETAIL_LEDE}</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {RETAIL.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.07}>
                <LoanCard item={item} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BUSINESS & COMMERCIAL FUNDING */}
      <section className="grain relative bg-navy py-16 sm:py-24 lg:py-28" data-testid="business-funding-section">
        <div className="container-x relative z-10">
          <Reveal>
            <p className="eyebrow !text-gold">Business & Commercial Funding</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Strategic capital for enterprises.
            </h2>
            <p className="mt-4 max-w-2xl text-white/70">{BUSINESS_LEDE}</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {BUSINESS.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.06}>
                <LoanCard item={item} dark />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* APPLY */}
      <section id="apply" className="py-16 sm:py-24" data-testid="loan-apply-section">
        <div className="container-x grid grid-cols-1 items-start gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow">Apply Now</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-navy sm:text-4xl lg:text-5xl">
              Ready to secure your capital?
            </h2>
            <p className="mt-5 max-w-md text-slate-600">
              Apply for a loan through the form and the funding desk calls you back with an eligibility read, a
              document list and the best-fit options — or check your eligibility instantly on WhatsApp.
            </p>
            <a
              href={`${CONTACT.whatsapp}?text=${encodeURIComponent("Hello Evolve Elite, I'd like to check my loan eligibility.")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:-translate-y-0.5 hover:brightness-110 active:scale-[0.98]"
              data-testid="loan-apply-whatsapp-btn"
            >
              <MessageCircle className="h-4 w-4" /> Check Your Eligibility via WhatsApp
            </a>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:p-8" data-testid="loan-apply-form-card">
              <LeadForm
                source="loans-page"
                fields={["name", "phone", "email", "service", "message"]}
                services={LOAN_SERVICES}
                cta="Apply for a Loan"
                idPrefix="loan-apply"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
